import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { addToast } from "@/core/lib/toast";
import type { InferHeyApiMutationOptions } from "@/core/lib/types";
import {
  getArticlesOptions,
  getArticleOptions,
  createArticleMutation,
  deleteArticleMutation,
  updateArticleMutation,
  publishArticleMutation,
  unpublishArticleMutation,
  incrementArticleViewsMutation,
  likeArticleMutation,
  dislikeArticleMutation,
} from "@/openapi/@tanstack/react-query.gen";
import type { ArticleResponse } from "@/openapi/types.gen";
import { m } from "@/paraglide/messages";

export function useArticles(filter?: {
  search?: string;
  topic?: string;
  published_only?: boolean;
}) {
  return useQuery({
    ...getArticlesOptions({ query: filter }),
  });
}

export function useArticle(id: string, enabled: boolean = true) {
  return useQuery({
    ...getArticleOptions({ path: { article_id: id } }),
    enabled: enabled && !!id,
  });
}

export function useCreateArticle(
  options: InferHeyApiMutationOptions<typeof createArticleMutation> = {},
) {
  const queryClient = useQueryClient();
  return useMutation({
    ...createArticleMutation(),
    ...options,
    meta: { errorMessage: m.toast_article_created_failed },
    onSuccess: async (data, variables, onMutateResult, context) => {
      await queryClient.invalidateQueries({
        queryKey: getArticlesOptions().queryKey,
      });

      await options?.onSuccess?.(data, variables, onMutateResult, context);
      addToast({
        title: m.toast_article_created_success(),
        severity: "success",
        duration: 2500,
      });
    },
  });
}

export function useDeleteArticle(
  options: InferHeyApiMutationOptions<typeof deleteArticleMutation> = {},
) {
  const queryClient = useQueryClient();
  return useMutation({
    ...deleteArticleMutation(),
    ...options,
    meta: { errorMessage: m.toast_article_deleted_failed },
    onSuccess: async (data, variables, onMutateResult, context) => {
      await queryClient.invalidateQueries({
        queryKey: getArticlesOptions().queryKey,
      });

      await options?.onSuccess?.(data, variables, onMutateResult, context);
      addToast({
        title: m.toast_article_deleted_success(),
        severity: "success",
        duration: 2500,
      });
    },
  });
}

export function useUpdateArticle(
  options: InferHeyApiMutationOptions<typeof updateArticleMutation> = {},
) {
  const queryClient = useQueryClient();
  return useMutation({
    ...updateArticleMutation(),
    ...options,
    meta: { errorMessage: m.toast_article_updated_failed },
    onSuccess: async (data, variables, onMutateResult, context) => {
      await queryClient.invalidateQueries({
        queryKey: getArticleOptions({ path: { article_id: data.id } }).queryKey,
      });

      await options?.onSuccess?.(data, variables, onMutateResult, context);
      addToast({
        title: m.toast_article_updated_success(),
        severity: "success",
        duration: 2500,
      });
    },
  });
}

export function useUpdateArticleContentPositions(articleId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    ...updateArticleMutation(),
    onMutate: async (variables) => {
      const { queryKey } = getArticleOptions({
        path: { article_id: articleId },
      });

      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey });

      // Snapshot the previous value
      const previousArticle = queryClient.getQueryData(queryKey);

      // Optimistically update the article
      queryClient.setQueryData(queryKey, (old: ArticleResponse | undefined) => {
        if (!old) return old;
        return {
          ...old,
          sections: variables.body.sections ?? old.sections,
        };
      });

      return { previousArticle };
    },
    onError: (_, __, context) => {
      // Rollback on error
      if (context?.previousArticle) {
        queryClient.setQueryData(
          getArticleOptions({ path: { article_id: articleId } }).queryKey,
          context.previousArticle,
        );
      }
    },
    onSuccess: async () => {
      // Background refetch to keep cache in sync
      await queryClient.invalidateQueries({
        queryKey: getArticleOptions({ path: { article_id: articleId } })
          .queryKey,
      });
    },
  });
}

export function usePublishArticle(
  options: InferHeyApiMutationOptions<typeof publishArticleMutation> = {},
) {
  const queryClient = useQueryClient();
  return useMutation({
    ...publishArticleMutation(),
    ...options,
    meta: { errorMessage: m.toast_article_published_failed },
    onSuccess: async (data, variables, onMutateResult, context) => {
      await queryClient.invalidateQueries({
        queryKey: getArticlesOptions().queryKey,
      });

      await options?.onSuccess?.(data, variables, onMutateResult, context);
      addToast({
        title: m.toast_article_published_success(),
        severity: "success",
        duration: 2500,
      });
    },
  });
}

export function useUnpublishArticle(
  options: InferHeyApiMutationOptions<typeof unpublishArticleMutation> = {},
) {
  const queryClient = useQueryClient();
  return useMutation({
    ...unpublishArticleMutation(),
    ...options,
    meta: { errorMessage: m.toast_article_unpublished_failed },
    onSuccess: async (data, variables, onMutateResult, context) => {
      await queryClient.invalidateQueries({
        queryKey: getArticlesOptions().queryKey,
      });

      await options?.onSuccess?.(data, variables, onMutateResult, context);
      addToast({
        title: m.toast_article_unpublished_success(),
        severity: "success",
        duration: 2500,
      });
    },
  });
}

export function useAddPositiveFeedback() {
  return useMutation({
    ...likeArticleMutation(),
    meta: { errorMessage: m.toast_failed_to_submit_feedback },
  });
}

export function useAddNegativeFeedback() {
  return useMutation({
    ...dislikeArticleMutation(),
    meta: { errorMessage: m.toast_failed_to_submit_feedback },
  });
}

export function useIncrementArticleViews() {
  return useMutation({
    ...incrementArticleViewsMutation(),
  });
}
